<main class="rt">

  <header class="rt__header">
    <div class="rt__heading">
      <h1>
        {{ 'realtime.title' | translate }}
        <span class="rt__live-dot" [class.is-on]="live && !error" [attr.aria-hidden]="true"></span>
      </h1>
      <p>{{ (isGlobal ? 'realtime.subtitleGlobal' : 'realtime.subtitle') | translate }}</p>
      <p class="rt__updated" *ngIf="lastUpdate && !error">
        {{ 'realtime.updatedAt' | translate:{ time: timeLabel(lastUpdate) } }}
      </p>
    </div>

    <div class="rt__controls">
      <div class="rt__range" role="group" [attr.aria-label]="'realtime.window.label' | translate">
        <button type="button"
                *ngFor="let m of windowOptions"
                class="rt__range-btn"
                [class.active]="windowMinutes === m"
                [disabled]="loading"
                (click)="selectWindow(m)">
          {{ 'realtime.window.minutes' | translate:{ count: m } }}
        </button>
      </div>
      <button type="button" class="rt__live-btn" [class.is-paused]="!live" (click)="toggleLive()" [disabled]="loading || !!error">
        {{ (live ? 'realtime.live.pause' : 'realtime.live.resume') | translate }}
      </button>
    </div>
  </header>

  <div class="rt__error" *ngIf="error">
    <p>{{ (error === 'forbidden' ? 'realtime.status.forbidden' : 'realtime.status.error') | translate }}</p>
    <button type="button" class="rt__retry" *ngIf="error === 'generic'" (click)="retry()">{{ 'realtime.actions.retry' | translate }}</button>
  </div>

  <ng-container *ngIf="!error">

    <section class="rt__section" [class.is-loading]="loading">

      <!-- Stats -->
      <div class="rt__stats">
        <div class="rt-stat">
          <span class="rt-stat__label">{{ 'realtime.stats.total' | translate:{ count: windowMinutes } }}</span>
          <span class="rt-stat__value">{{ loading ? '' : formatNumber(totalEvents) }}</span>
        </div>
        <div class="rt-stat">
          <span class="rt-stat__label">{{ 'realtime.stats.lastMinute' | translate }}</span>
          <span class="rt-stat__value">{{ loading ? '' : formatNumber(lastMinuteEvents) }}</span>
        </div>
        <div class="rt-stat">
          <span class="rt-stat__label">{{ 'realtime.stats.perSecond' | translate }}</span>
          <span class="rt-stat__value">{{ loading ? '' : formatNumber(+(lastMinuteEvents / 60).toFixed(1)) }}</span>
        </div>
        <div class="rt-stat">
          <span class="rt-stat__label">{{ 'realtime.stats.sampled' | translate }}</span>
          <span class="rt-stat__value">{{ loading ? '' : formatNumber(sampledShown) }}</span>
        </div>
      </div>

      <!-- Active filters (drill-down path) -->
      <div class="rt__filters" *ngIf="hasFilters">
        <span class="rt__filters-label">{{ 'realtime.filters.label' | translate }}</span>
        <button type="button" class="rt-chip" *ngIf="filters.entity" (click)="clearFilter('entity')">
          {{ 'realtime.dims.entity' | translate }}: {{ entityLabel(filters.entity) }} <span aria-hidden="true">×</span>
        </button>
        <button type="button" class="rt-chip" *ngIf="filters.provider !== null" (click)="clearFilter('provider')">
          {{ 'realtime.dims.provider' | translate }}: {{ providerLabel(filters.provider) }} <span aria-hidden="true">×</span>
        </button>
        <button type="button" class="rt-chip" *ngIf="filters.sourceType !== null" (click)="clearFilter('sourceType')">
          {{ 'realtime.dims.sourceType' | translate }}: {{ sourceTypeLabel(filters.sourceType) }} <span aria-hidden="true">×</span>
        </button>
        <button type="button" class="rt-chip" *ngIf="filters.minute !== null" (click)="clearFilter('minute')">
          {{ 'realtime.filters.minute' | translate }}: {{ minuteLabel(filters.minute!) }} <span aria-hidden="true">×</span>
        </button>
        <button type="button" class="rt__clear" (click)="clearAllFilters()">{{ 'realtime.filters.clear' | translate }}</button>
      </div>

      <div class="rt__grid">
        <!-- Trend -->
        <div class="rt-card rt-card--wide">
          <div class="rt-card__header">
            <h3>{{ 'realtime.chart.title' | translate }}</h3>
            <div class="rt-toggle">
              <button type="button" *ngFor="let d of availableDims"
                      [class.active]="breakdownDim === d"
                      (click)="setBreakdown(d)">
                {{ 'realtime.dims.' + d | translate }}
              </button>
            </div>
          </div>
          <div class="rt-card__body rt-card__body--chart">
            <canvas #trendCanvas></canvas>
            <div class="rt-empty" *ngIf="!loading && totalEvents === 0 && filters.minute === null">{{ 'realtime.status.noData' | translate }}</div>
          </div>
          <div class="rt-card__hint">{{ 'realtime.chart.hint' | translate }}</div>
        </div>

        <!-- Breakdown -->
        <div class="rt-card">
          <div class="rt-card__header">
            <h3>{{ 'realtime.breakdown.title' | translate:{ dim: ('realtime.dims.' + breakdownDim | translate) } }}</h3>
          </div>
          <div class="rt-card__body rt-card__body--list">
            <div class="rt-empty rt-empty--static" *ngIf="!loading && breakdownRows.length === 0">{{ 'realtime.status.noData' | translate }}</div>
            <button type="button" class="rt-ranked" *ngFor="let row of breakdownRows"
                    [class.active]="breakdownDim === 'sourceType' && filters.sourceType === row.key"
                    (click)="drill(row)" [title]="row.label">
              <span class="rt-ranked__name">{{ row.label }}</span>
              <span class="rt-ranked__track"><span class="rt-ranked__fill" [style.width.%]="row.pct"></span></span>
              <span class="rt-ranked__value">{{ formatNumber(row.count) }}</span>
            </button>
          </div>
        </div>

        <!-- Sample table -->
        <div class="rt-card rt-card--table">
          <div class="rt-card__header">
            <h3>{{ (isSampled ? 'realtime.table.titleSampled' : 'realtime.table.title') | translate }}</h3>
            <span class="rt-card__meta" *ngIf="truncated">{{ 'realtime.table.truncated' | translate }}</span>
          </div>
          <!-- Sampling applies to these raw rows only: counts, chart and breakdown above are exact. -->
          <div class="rt__sampling" *ngIf="!loading && isSampled">
            <strong>{{ 'realtime.sampling.title' | translate:{ pct: formatPct(sampledPct) } }}</strong>
            <span>{{ 'realtime.sampling.detail' | translate:{ shown: formatNumber(sampledShown), total: formatNumber(totalEvents) } }}</span>
          </div>
          <div class="rt-table-wrap">
            <table class="rt-table">
              <thead>
                <tr>
                  <th>{{ 'realtime.table.time' | translate }}</th>
                  <th *ngIf="isGlobal">{{ 'realtime.dims.entity' | translate }}</th>
                  <th>{{ 'realtime.dims.provider' | translate }}</th>
                  <th>{{ 'realtime.dims.sourceType' | translate }}</th>
                  <th>TNCID</th>
                  <th>CrmID</th>
                  <th>ExternalUID</th>
                  <th>{{ 'realtime.table.email' | translate }}</th>
                  <th>{{ 'realtime.table.phone' | translate }}</th>
                  <th class="rt-table__num">{{ 'realtime.table.customVars' | translate }}</th>
                  <th *ngIf="canUnmask" class="rt-table__icon"></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of pagedRows" (click)="openDetail(row)" [class.active]="detailRow?.EID === row.EID">
                  <td class="rt-mono">{{ timeLabel(row.CreationTime) }}</td>
                  <td *ngIf="isGlobal" class="rt-ellipsis" [title]="entityLabel(row.EntityID)">{{ entityLabel(row.EntityID) }}</td>
                  <td class="rt-ellipsis" [title]="providerLabel(row.ProviderID)">{{ providerLabel(row.ProviderID) }}</td>
                  <td>{{ sourceTypeLabel(row.SourceType) }}</td>
                  <td class="rt-mono rt-ellipsis" [title]="row.TNCID || ''">{{ row.TNCID || '—' }}</td>
                  <ng-container *ngFor="let f of ['CrmID', 'ExternalUID', 'Sha256Email', 'Sha256Phone']">
                    <td class="rt-mono rt-ellipsis" [class.rt-masked]="isMasked(cell(row, $any(f)))" [title]="isMasked(cell(row, $any(f))) ? '' : (cell(row, $any(f)) || '')">
                      {{ cell(row, $any(f)) || '—' }}
                    </td>
                  </ng-container>
                  <td class="rt-table__num">
                    <span class="rt-badge" *ngIf="row.CustomVarCount > 0; else noCustomVars"
                          [title]="'realtime.table.customVarsHint' | translate:{ count: row.CustomVarCount }">{{ row.CustomVarCount }}</span>
                    <ng-template #noCustomVars>—</ng-template>
                  </td>
                  <td *ngIf="canUnmask" class="rt-table__icon">
                    <button type="button" class="rt-eye" *ngIf="hasMaskedValues(row) && !isRevealed(row)"
                            [title]="'realtime.actions.reveal' | translate" (click)="revealRow(row, $event)">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="rt-empty rt-empty--static" *ngIf="!loading && visibleRows.length === 0">{{ 'realtime.table.empty' | translate }}</div>
          </div>
          <div class="rt-card__footer" *ngIf="visibleRows.length > pagedRows.length">
            <button type="button" class="rt__clear" (click)="showMoreRows()">
              {{ 'realtime.table.more' | translate:{ shown: pagedRows.length, total: visibleRows.length } }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </ng-container>

  <!-- Detail drawer -->
  <div class="rt-drawer-backdrop" *ngIf="detailRow" (click)="closeDetail()"></div>
  <aside class="rt-drawer" *ngIf="detailRow" role="dialog" [attr.aria-label]="'realtime.detail.title' | translate">
    <div class="rt-drawer__header">
      <div>
        <h3>{{ 'realtime.detail.title' | translate }}</h3>
        <p class="rt-mono">{{ detailRow.EID }}</p>
      </div>
      <button type="button" class="rt-drawer__close" (click)="closeDetail()" [attr.aria-label]="'realtime.actions.close' | translate">×</button>
    </div>

    <div class="rt-drawer__summary">
      <div><span>{{ 'realtime.table.time' | translate }}</span><strong>{{ timeLabel(detailRow.CreationTime) }}</strong></div>
      <div><span>{{ 'realtime.dims.entity' | translate }}</span><strong>{{ detail?.EntityName || entityLabel(detailRow.EntityID) }}</strong></div>
      <div><span>{{ 'realtime.dims.provider' | translate }}</span><strong>{{ detail?.ProviderName || providerLabel(detailRow.ProviderID) }}</strong></div>
      <div><span>{{ 'realtime.dims.sourceType' | translate }}</span><strong>{{ sourceTypeLabel(detailRow.SourceType) }}</strong></div>
    </div>

    <div class="rt-drawer__mask" *ngIf="detail">
      <ng-container *ngIf="detail.Masked; else unmaskedTpl">
        <span>{{ 'realtime.detail.masked' | translate }}</span>
        <button type="button" class="rt__live-btn" *ngIf="detail.CanUnmask" (click)="unmaskDetail()" [disabled]="detailLoading">
          {{ 'realtime.actions.reveal' | translate }}
        </button>
      </ng-container>
      <ng-template #unmaskedTpl><span>{{ 'realtime.detail.unmasked' | translate }}</span></ng-template>
    </div>

    <div class="rt-drawer__body">
      <div class="rt-drawer__status" *ngIf="detailLoading">{{ 'realtime.detail.loading' | translate }}</div>
      <div class="rt-drawer__status rt-drawer__status--error" *ngIf="detailError">{{ detailError }}</div>

      <ng-container *ngIf="detail && !detailLoading">
        <section class="rt-drawer__section">
          <h4>{{ 'realtime.detail.identifiers' | translate }}</h4>
          <table class="rt-kv">
            <tr *ngFor="let f of detailIdentifiers">
              <th>{{ f.key }}</th>
              <td class="rt-mono" [class.rt-masked]="isMasked(f.value)">{{ f.value || '—' }}</td>
            </tr>
          </table>
        </section>

        <section class="rt-drawer__section" *ngIf="detailCustomVars.length">
          <h4>
            {{ 'realtime.detail.customVariables' | translate }}
            <span class="rt-drawer__count">{{ detailCustomVars.length }}</span>
          </h4>
          <table class="rt-kv">
            <tr *ngFor="let cv of detailCustomVars">
              <th>{{ cv.Name }}</th>
              <td class="rt-mono">{{ cv.Value || '—' }}</td>
            </tr>
          </table>
        </section>

        <!-- Every browser_data column: TNC-internal detail, only for admin_dashboard.all (enforced by the API) -->
        <section class="rt-drawer__section" *ngIf="detail.AllFields">
          <h4>
            {{ 'realtime.detail.fields' | translate }}
            <span class="rt-drawer__count">{{ detailFields.length }}</span>
          </h4>
          <table class="rt-kv">
            <tr *ngFor="let f of detailFields">
              <th>{{ f.key }}</th>
              <td class="rt-mono" [class.rt-masked]="isMasked(f.value)">{{ f.value }}</td>
            </tr>
          </table>
        </section>
      </ng-container>
    </div>
  </aside>
</main>
